import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Loader2, ShieldAlert, KeyRound } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { PbrMark, PbrNotice } from '../../components/cultivar';
import cultivarService from '../../services/cultivar';
import {
  CULTIVAR_ORDER_STATUS_LABELS,
  type CultivarOrder,
  type CultivarOrderStatus,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

const STATUS_FILTERS: Array<{ value: '' | CultivarOrderStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'nursery_accepted', label: 'Nursery accepted' },
  { value: 'awaiting_customer', label: 'Awaiting customer' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-800',
  nursery_accepted: 'bg-indigo-100 text-indigo-800',
  awaiting_approvers: 'bg-amber-100 text-amber-800',
  awaiting_customer: 'bg-amber-100 text-amber-800',
  customer_signed: 'bg-emerald-100 text-emerald-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  fulfilled: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-red-100 text-red-700',
  accepted: 'bg-gray-100 text-gray-700',
};

function fmtMoney(v: string | number | null, currency: string): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `${currency || 'AUD'} ${n.toFixed(2)}`;
}

function OrderList() {
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [orders, setOrders] = useState<CultivarOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | CultivarOrderStatus>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await cultivarService.listOrderingOrders(
        status ? { status, role: 'nursery' } : { role: 'nursery' }
      );
      if (resp.success && resp.data) setOrders(resp.data as CultivarOrder[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  if (licenseLoading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar PBR seat required</h3>
            <p className="text-sm text-gray-600">Placing cultivar orders requires a cultivar PBR or admin licence seat.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Cultivar orders"
        subtitle="Orders your nursery has placed on behalf of final customers."
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value as '' | CultivarOrderStatus)}
            className="border rounded px-2 py-1.5 text-sm">
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate('/cultivar/orders/api-keys')}>
              <KeyRound className="w-4 h-4 mr-2" /> API keys
            </Button>
            <Button onClick={() => navigate('/cultivar/orders/new')}>
              <Plus className="w-4 h-4 mr-2" /> Place order
            </Button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}

        <PbrNotice />

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">#</th>
                    <th className="px-4 py-2">Cultivar</th>
                    <th className="px-4 py-2">Customer</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right whitespace-nowrap">Total ex GST</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No orders yet.</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link className="text-blue-700 hover:underline" to={`/cultivar/orders/${o.id}`}>{o.id}</Link>
                      </td>
                      <td className="px-4 py-3">
                        {o.cultivar_name}
                        <PbrMark status={o.cultivar_protection_status ?? null} />
                        {o.cultivar_trade_name ? ` (${o.cultivar_trade_name})` : ''}
                      </td>
                      <td className="px-4 py-3">{o.customer_company || '—'}</td>
                      <td className="px-4 py-3 text-right">{o.qty_trees}</td>
                      <td className="px-4 py-3 text-right">{fmtMoney(o.frozen_total, o.currency)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[o.status] || 'bg-gray-100'}`}>
                          {CULTIVAR_ORDER_STATUS_LABELS[o.status] || o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{o.created_at ? o.created_at.slice(0, 10) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default OrderList;
